<template>
	<div class="cog-square-payment">
		<c-square-card
			ref="card"
			:application-id="apiKey"
			:locale="$culture.name"
			:input-styles="inputStyles"
			:save-card="saveCard"
			:location-id="locationId"
			:location-currency-code="locationCurrencyCode"
			:payment-sdk-url="paymentSdkUrl"
			:suppress-buyer-verification="suppressBuyerVerification"
			@ready="$emit('ready')"
			@cardEvent="handleCardEvent"
			@authentication-complete="authenticationComplete"
		>
			<div id="card-container" ref="cardContainer" class="cog-square-card cog-payment__frame">
				<div class="cog-input__placeholder" @click="$refs.card.initializeForm()"></div>
			</div>
		</c-square-card>
	</div>
</template>

<script>
	import CSquareCard from './SquareCard';
	import ResizeObserver from 'resize-observer-polyfill';

	export default {
		name: 'CSquarePayment',
		components: {
			'c-square-card': CSquareCard
		},
		inject: ['$resource', 'flags', '$culture'],
		props: {
			entry: { type: Object, required: true },
			orderInfo: { type: Object, default: null },
			apiKey: { type: String, required: true },
			inputStyles: { type: Object, default: null },
			saveCard: { type: Boolean, default: false },
			paymentRequired: { type: Boolean, default: false },
			locationId: { type: String, default: null },
			locationCurrencyCode: { type: String, default: null },
			paymentSdkUrl: { type: String, default: null },
			suppressBuyerVerification: { type: Boolean, default: false }
		},
		data() {
			return {
				resizeObserver: new ResizeObserver(() => {
					if (this.$refs.card.card)
						this.$refs.card.card.recalculateSize();
				})
			};
		},
		computed: {
			order() {
				return this.entry.Order;
			},
			paymentToken() {
				return this.entry.Entry.PaymentToken;
			}
		},
		async mounted() {
			this.resizeObserver.observe(this.$el);
		},
		beforeDestroy() {
			this.resizeObserver.unobserve(this.$el);
		},
		methods: {
			/**
			 * @param event { import('@square/web-payments-sdk-types').SqEvent<import('@square/web-payments-sdk-types').CardInputEvent> }
			 */
			handleCardEvent(event) {
				if (event.type === 'focusClassAdded') {
					// Add focus class to container
					const focussedElement = this.$refs.cardContainer;
					if (focussedElement) {
						focussedElement.classList.add('c-square-input--focus');
					}
					/**
					 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
					 */
					const focusEvent = {
						type: 'focus',
						sender: this,
						originalEvent: event,
						originalSender: this.$refs.form
					};
					this.$emit('focus', focusEvent);
				}
				else if (event.type === 'focusClassRemoved') {
					// Remove focus class from container
					const focussedElement = this.$refs.cardContainer;
					if (focussedElement) {
						focussedElement.classList.remove('c-square-input--focus');
					}
					/**
					 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
					 */
					const blurEvent = {
						type: 'blur',
						sender: this,
						originalEvent: event,
						originalSender: this.$refs.form
					};
					this.$emit('blur', blurEvent);
				}
			},
			/**
			 * @param orderInfo { import('../../framework/payment/payment-types').OrderInfo } - The order information
			 * @returns { Promise<import('../../framework/payment/payment-types').PaymentTokenInfo> } - The token that was created
			 */
			async createToken() {
				/**
				 * @type { import('./SquareCard').default }
				 */
				const cardRef = this.$refs.card;

				const token = await cardRef.submitPayment(this.orderInfo, this.saveCard ? 'STORE' : 'CHARGE');

				return token;
			},

			async authenticate() {
				if (!this.paymentToken) return;

				if (this.paymentToken.Type !== 'CustomerCard') {
					this.$emit('authentication-complete', { success: false });
					return;
				}
				/**
				 * @type { import('./SquareCard').default }
				 */
				const cardRef = this.$refs.card;
				await cardRef.verifyBuyerCharge(this.order, this.paymentToken.Token);
			},

			authenticationComplete(event) {
				this.authenticationPaymentToken = null;

				if (event.token) {
					// Update the payment token on the entry
					this.entry.Entry.PaymentToken.update({
						Status: event.status,
						Type: event.type,
						Token: event.token,
						ClientSecret: event.clientSecret,
						VerificationToken: event.verificationToken
					});
				}

				// On failure, show a payment error
				if (!event.success)
					this.paymentError = this.$resource('payment-declined') + ': ' + this.$resource('credit-card-authentication-failed');

				// Only the success flag (and optional resubmit) is needed beyond this point
				this.$emit('authentication-complete', { success: event.success, forceResubmit: event.forceResubmit });
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-square-payment {

		.cog-input__placeholder,
		.sq-card-component {
			background: var(--input__background-color);
			border-width:
				var(--input__border-width-top, var(--input__border-width))
				var(--input__border-width-right, var(--input__border-width))
				var(--input__border-width-bottom, var(--input__border-width))
				var(--input__border-width-left, var(--input__border-width));
			border-style: var(--input__border-style);
			border-color: var(--input__border-color);
			border-radius: var(--input__border-radius);
			box-shadow: var(--input__box-shadow);
		}

		// If sq-card-component is on two lines, use the top border as the bottom
		// If there is no top border there should be no bottom border.
		.sq-card-iframe-container[style*='height: 97px'] .sq-card-component {
			border-bottom-width: var(--input__border-width-top, var(--input__border-width));
		}

		// Give the placeholder a height that is approximately what the Square card will be
		.cog-square-card .cog-input__placeholder {
			height: 97px;
		}

		.sq-card-message {
			display: none;
		}

		.sq-focus .sq-card-component {
			border-color: var(--highlight);
			box-shadow: var(--input-focus__box-shadow);
			transition: border-color .3s, box-shadow .3s;
		}

		.sq-focus.sq-error .sq-card-component {
			border-color: var(--negative);
		}
	}
}
</style>